import { useState } from 'react';
import toast from 'react-hot-toast';
import { FaEnvelope, FaSms } from 'react-icons/fa';

export default function PayByLinkPopup({ orderData, isTabletView, onClose, onSuccess, paymentAmount }) {
    const [selectedChannel, setSelectedChannel] = useState('email');
    const [isLoading, setIsLoading] = useState(false);

    const displayAmount = paymentAmount !== undefined && paymentAmount !== null
        ? Number(paymentAmount).toFixed(2)
        : Number(orderData?.total_price || 0).toFixed(2);

    const handleSubmit = async () => {
        if (!selectedChannel) {
            return toast.error('Please select a channel (SMS or Email)');
        }

        setIsLoading(true);
        try {
            const response = await onSuccess(selectedChannel);
            if (response?.status === 'success') {
                toast.success(`Payment link sent via ${selectedChannel.toUpperCase()}`);
                onClose();
            } else {
                toast.error(response?.message || 'Failed to send payment link');
            }
        } catch (error) {
            console.error('Error sending payment link:', error);
            toast.error('Failed to send payment link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center">
            <div className={`${isTabletView ? 'w-[480px]' : 'w-[550px]'} bg-color2 border-2 border-color3 rounded-md p-6`}>
                {/* Header */}
                <div className={`mb-6 text-center`}>
                    <h2 className={`font-bold text-color6 ${isTabletView ? 'text-lg' : 'text-xl'}`}>
                        Send Payment Link
                    </h2>
                    <p className={`${isTabletView ? 'text-xs mt-1' : 'text-sm mt-2'} text-color5`}>
                        Order #{orderData?.id || orderData?.order_id || ""} - £{displayAmount}
                    </p>
                </div>

                {/* Channel Selection */}
                <div className={`mb-6 space-y-3`}>
                    {/* Email Option */}
                    <button
                        onClick={() => setSelectedChannel('email')}
                        className={`w-full p-4 rounded-lg border-2 transition-all ${
                            selectedChannel === 'email'
                                ? 'border-green-500 bg-color2'
                                : 'border-color3 bg-color3 hover:border-green-400'
                        }`}
                    >
                        <div className={`flex items-center gap-4`}>
                            <div className={`${selectedChannel === 'email' ? 'text-green-500' : 'text-color6'} text-2xl`}>
                                <FaEnvelope />
                            </div>
                            <div className={`text-left flex-1`}>
                                <h3 className={`font-semibold ${isTabletView ? 'text-sm' : 'text-base'}`}>
                                    Send via Email
                                </h3>
                                <p className={`${isTabletView ? 'text-xs' : 'text-sm'} text-color5 mt-0.5`}>
                                    {orderData?.customer?.email || 'customer@email.com'}
                                </p>
                            </div>
                            <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                    selectedChannel === 'email'
                                        ? 'border-green-500 bg-color2'
                                        : 'border-color5'
                                }`}
                            >
                                {selectedChannel === 'email' && (
                                    <div className="w-2 h-2 bg-white rounded-full" />
                                )}
                            </div>
                        </div>
                    </button>

                    {/* SMS Option */}
                    <button
                        onClick={() => setSelectedChannel('sms')}
                        className={`w-full p-4 rounded-lg border-2 transition-all ${
                            selectedChannel === 'sms'
                                ? 'border-green-500 bg-color2'
                                : 'border-color3 bg-color3 hover:border-green-400'
                        }`}
                    >
                        <div className={`flex items-center gap-4`}>
                            <div className={`${selectedChannel === 'sms' ? 'text-green-500' : 'text-color6'} text-2xl`}>
                                <FaSms />
                            </div>
                            <div className={`text-left flex-1`}>
                                <h3 className={`font-semibold ${isTabletView ? 'text-sm' : 'text-base'}`}>
                                    Send via SMS
                                </h3>
                                <p className={`${isTabletView ? 'text-xs' : 'text-sm'} text-color5 mt-0.5`}>
                                    {orderData?.customer?.number || orderData?.customer_number || '+1234567890'}
                                </p>
                            </div>
                            <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                    selectedChannel === 'sms'
                                        ? 'border-green-500 bg-green-500'
                                        : 'border-color5'
                                }`}
                            >
                                {selectedChannel === 'sms' && (
                                    <div className="w-3 h-3 bg-white rounded-full" />
                                )}
                            </div>
                        </div>
                    </button>
                </div>

                {/* Info Message */}
                <div className={`mb-6 p-3 bg-color2 border border-blue-200 rounded-md`}>
                    <p className={`${isTabletView ? 'text-xs' : 'text-sm'} text-color6`}>
                        💡 The payment link will be sent to the customer's {selectedChannel === 'email' ? 'email' : 'phone number'}. 
                        They can complete the payment through the link.
                    </p>
                </div>

                {/* Action Buttons */}
                <div className={`flex gap-3 justify-end`}>
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className={`px-6 py-2.5 rounded-md font-semibold border-2 border-color3 text-color6 transition-all
                            ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-color3'}`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className={`px-6 py-2.5 rounded-md font-semibold bg-blue-500 text-white transition-all
                            ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
                    >
                        {isLoading ? 'Sending...' : 'Send Link'}
                    </button>
                </div>
            </div>
        </div>
    );
}
